import { create } from 'zustand';
import type { CSSProperties } from 'react';

/**
 * Canvas-grid personalization (Vivarium styleguide §04).
 *
 * Factors the render/CanvasSurface 32px line-grid into one switchable recipe +
 * a persisted choice. It does NOT add a new layer — the surface already draws a
 * pan/zoom-synced grid via gridSize/gridX/gridY; this just swaps the recipe.
 * Default stays 'line' so existing users see no change.
 *
 * Every recipe is parameterized by the live gridSize/gridX/gridY so it pans and
 * zooms with the canvas, and uses var(--color-grid) (minor) so it recolors with
 * the theme for free. Blueprint majors use the accent token.
 */
export const GRID_KINDS = ['line', 'dot', 'blueprint', 'iso', 'cross', 'none'] as const;
export type GridKind = (typeof GRID_KINDS)[number];

const KEY = 'codeframe.canvas-grid';

function readInitial(): GridKind {
  if (typeof localStorage === 'undefined') return 'line';
  const v = localStorage.getItem(KEY);
  return (GRID_KINDS as readonly string[]).includes(v ?? '') ? (v as GridKind) : 'line';
}

interface GridStore {
  kind: GridKind;
  setKind: (k: GridKind) => void;
}

export const useCanvasGrid = create<GridStore>((set) => ({
  kind: readInitial(),
  setKind: (k) => {
    if (typeof localStorage !== 'undefined') {
      try { localStorage.setItem(KEY, k); } catch { /* quota / disabled */ }
    }
    set({ kind: k });
  },
}));

const MINOR = 'var(--color-grid)';
const MAJOR = 'color-mix(in oklab, var(--color-accent) 40%, transparent)';

/**
 * Background-layer style for a grid kind at the current pan/zoom.
 * @param gridSize  cell size in screen px (= 32 * zoom)
 * @param gridX/gridY  pan offset modulo the cell (keeps the grid registered)
 */
export function gridLayers(kind: GridKind, gridSize: number, gridX: number, gridY: number): CSSProperties {
  const size = `${gridSize}px ${gridSize}px`;
  const pos = `${gridX}px ${gridY}px`;
  const base: CSSProperties = { backgroundColor: 'var(--color-bg)' };

  switch (kind) {
    case 'none':
      return base;

    case 'dot':
      return {
        ...base,
        backgroundImage: `radial-gradient(${MINOR} 1.2px, transparent 1.2px)`,
        backgroundSize: size,
        backgroundPosition: pos,
      };

    case 'blueprint': {
      // minor 1px lines on every cell + accent majors every 4th cell
      const major = `${gridSize * 4}px ${gridSize * 4}px`;
      return {
        ...base,
        backgroundImage:
          `linear-gradient(to right, ${MINOR} 1px, transparent 1px),` +
          `linear-gradient(to bottom, ${MINOR} 1px, transparent 1px),` +
          `linear-gradient(to right, ${MAJOR} 1px, transparent 1px),` +
          `linear-gradient(to bottom, ${MAJOR} 1px, transparent 1px)`,
        backgroundSize: `${size}, ${size}, ${major}, ${major}`,
        backgroundPosition: `${pos}, ${pos}, ${pos}, ${pos}`,
      };
    }

    case 'iso':
      // isometric: three line families at ±30° and vertical, period = gridSize
      return {
        ...base,
        backgroundImage:
          `repeating-linear-gradient(30deg, ${MINOR} 0 1px, transparent 1px ${gridSize}px),` +
          `repeating-linear-gradient(150deg, ${MINOR} 0 1px, transparent 1px ${gridSize}px),` +
          `repeating-linear-gradient(90deg, ${MINOR} 0 1px, transparent 1px ${gridSize}px)`,
        backgroundSize: size,
        backgroundPosition: pos,
      };

    case 'cross':
      // diagonal crosshatch (X) — distinct from iso's parallel families
      return {
        ...base,
        backgroundImage:
          `repeating-linear-gradient(45deg, ${MINOR} 0 1px, transparent 1px ${gridSize}px),` +
          `repeating-linear-gradient(-45deg, ${MINOR} 0 1px, transparent 1px ${gridSize}px)`,
        backgroundSize: size,
        backgroundPosition: pos,
      };

    case 'line':
    default:
      // the exact legacy recipe — current behavior, preserved
      return {
        ...base,
        backgroundImage:
          `linear-gradient(to right, ${MINOR} 1px, transparent 1px),` +
          `linear-gradient(to bottom, ${MINOR} 1px, transparent 1px)`,
        backgroundSize: size,
        backgroundPosition: pos,
      };
  }
}
